<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-clock-outline me-2"></i>{{ $t('page.cron', 'Cron / Scheduled Jobs') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-warning" @click="runTick" :disabled="ticking"><i class="mdi mdi-play"></i> {{ ticking?'Ticking…':'Run tick now' }}</button>
          <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <!-- KPI -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num">{{ stats.total || 0 }}</div><div class="hms-stat-lbl">Total jobs</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.enabled || 0 }}</div><div class="hms-stat-lbl">Enabled</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ stats.disabled || 0 }}</div><div class="hms-stat-lbl">Disabled</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.failing || 0 }}</div><div class="hms-stat-lbl">Last-run failed</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.chronic_fail || 0 }}</div><div class="hms-stat-lbl">Chronic (≥3)</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='jobs' }" @click="tab='jobs'"><i class="mdi mdi-format-list-bulleted me-1"></i>Jobs</button>
        <button class="hms-tab" :class="{ active: tab==='runs' }" @click="tab='runs'; loadRuns(null)"><i class="mdi mdi-history me-1"></i>Run log</button>
        <button class="hms-tab" :class="{ active: tab==='setup' }" @click="tab='setup'"><i class="mdi mdi-server me-1"></i>Server setup</button>
      </div>

      <!-- ===== JOBS ===== -->
      <div v-if="tab==='jobs'">
        <table class="hms-table">
          <thead>
            <tr>
              <th>Job</th><th>Handler</th><th>Schedule</th>
              <th>Last run</th><th>Output</th><th>Next</th><th>Runs</th><th>Enabled</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="j in jobs" :key="j.job_id" :class="rowClass(j)">
              <td>
                <strong>{{ j.job_name }}</strong>
                <span v-if="j.is_system==1" class="hms-pill hms-pill-info ms-1">system</span>
                <div class="hms-muted-sm"><code>{{ j.job_code }}</code></div>
                <div v-if="j.description" class="hms-muted-sm">{{ j.description }}</div>
              </td>
              <td><code>{{ j.handler }}</code></td>
              <td>{{ formatSchedule(j) }}</td>
              <td class="hms-muted-sm">
                <span v-if="j.last_run_status" class="hms-pill" :class="statusPill(j.last_run_status)">{{ j.last_run_status }}</span>
                <div v-if="j.last_run_at">{{ formatDt(j.last_run_at) }}</div>
                <div v-if="j.last_run_ms" class="hms-muted-sm">{{ j.last_run_ms }}ms</div>
              </td>
              <td class="hms-output-cell">{{ j.last_run_output || '—' }}</td>
              <td class="hms-muted-sm">{{ formatDt(j.next_run_at) }}</td>
              <td class="text-center">
                <strong>{{ j.run_count || 0 }}</strong>
                <div v-if="j.fail_count > 0" class="hms-muted-sm st-bad">{{ j.fail_count }} fail</div>
              </td>
              <td>
                <label class="hms-switch">
                  <input type="checkbox" :checked="j.is_enabled==1" @change="toggle(j)" />
                  <span class="hms-slider"></span>
                </label>
              </td>
              <td>
                <button class="hms-btn hms-btn-success hms-btn-sm" @click="trigger(j)"><i class="mdi mdi-play"></i> Run</button>
                <button class="hms-btn-link" @click="loadRuns(j); tab='runs'" title="View runs"><i class="mdi mdi-history"></i></button>
                <button class="hms-btn-link" @click="openEdit(j)" title="Edit schedule"><i class="mdi mdi-pencil"></i></button>
              </td>
            </tr>
            <tr v-if="!jobs.length"><td colspan="9" class="text-center hms-pad hms-muted">No jobs.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- ===== RUNS ===== -->
      <div v-if="tab==='runs'">
        <div class="d-flex gap-2 mb-2">
          <select v-model="runsFilter" class="hms-input" @change="loadRunsByCode" style="max-width:280px">
            <option value="">All jobs</option>
            <option v-for="j in jobs" :key="j.job_id" :value="j.job_id">{{ j.job_code }}</option>
          </select>
        </div>
        <table class="hms-table">
          <thead><tr><th>Job</th><th>Started</th><th>Duration</th><th>Status</th><th>Trigger</th><th>Output</th></tr></thead>
          <tbody>
            <tr v-for="r in runs" :key="r.run_id" :class="r.status==='failed' ? 'row-action' : (r.status==='success'?'row-good':'')">
              <td><code>{{ r.job_code }}</code></td>
              <td class="hms-muted-sm">{{ formatDt(r.started_at) }}</td>
              <td>{{ r.duration_ms || '—' }}ms</td>
              <td><span class="hms-pill" :class="statusPill(r.status)">{{ r.status }}</span></td>
              <td><span class="hms-pill hms-pill-muted">{{ r.triggered_by }}</span></td>
              <td class="hms-output-cell">{{ r.output_summary || r.error_text || '—' }}</td>
            </tr>
            <tr v-if="!runs.length"><td colspan="6" class="text-center hms-pad hms-muted">No runs yet.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- ===== SETUP ===== -->
      <div v-if="tab==='setup'">
        <div class="hms-card" style="max-width:780px">
          <div class="hms-card-head">Server cron setup</div>
          <div class="hms-card-body">
            <p>Add this to your server's crontab (<code>crontab -e</code>) to fire the dispatcher every minute:</p>
            <pre class="hms-pre">* * * * * curl -s -X POST '{{ apiBase }}?function=runCronTick&cron_token=YOUR_SECRET' &gt; /dev/null 2&gt;&amp;1</pre>
            <p>Or as a wget:</p>
            <pre class="hms-pre">* * * * * wget -qO- '{{ apiBase }}?function=runCronTick&cron_token=YOUR_SECRET'</pre>
            <p>Set <code>HMS_CRON_TOKEN</code> in your server environment (e.g. nginx or .htaccess) to match the secret. Default is <code>hms-cron-default-secret</code> — <strong>change this in production</strong>.</p>

            <div class="hms-section-lbl mt-3">Manual tick (this admin)</div>
            <p>Use the <strong>Run tick now</strong> button at the top to dispatch all due jobs once — uses your bearer token, no cron token required.</p>

            <div class="hms-section-lbl mt-3">Adding new jobs</div>
            <p>Insert a row in <code>cron_jobs</code> with a <code>handler</code> matching a PHP function name (e.g. <code>cron_my_handler</code>). Handler signature: <code>function cron_my_handler($hospId, $branchId)</code>, returning <code>['ok' =&gt; bool, 'msg' =&gt; string]</code>.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== EDIT MODAL ===== -->
    <div v-if="editModal" class="hms-modal-bg" @click.self="editModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>Edit job: {{ form.job_code }}</h5><button class="hms-icon-btn" @click="editModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-12"><label class="hms-label">Job name</label><input v-model="form.job_name" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Description</label><textarea v-model="form.description" class="hms-input" rows="2"></textarea></div>
            <div class="col-6"><label class="hms-label">Schedule type</label>
              <select v-model="form.schedule_type" class="hms-input">
                <option value="interval_minutes">Every N minutes</option>
                <option value="daily_at_time">Daily at time</option>
                <option value="weekly">Weekly</option>
                <option value="manual">Manual only</option>
              </select>
            </div>
            <div v-if="form.schedule_type==='interval_minutes'" class="col-6"><label class="hms-label">Interval (minutes)</label><input v-model.number="form.interval_minutes" type="number" min="1" class="hms-input" /></div>
            <div v-if="form.schedule_type==='daily_at_time' || form.schedule_type==='weekly'" class="col-6"><label class="hms-label">Time</label><input v-model="form.schedule_time" type="time" class="hms-input" /></div>
            <div v-if="form.schedule_type==='weekly'" class="col-12"><label class="hms-label">Day</label>
              <select v-model.number="form.day_of_week" class="hms-input">
                <option :value="0">Sunday</option><option :value="1">Monday</option>
                <option :value="2">Tuesday</option><option :value="3">Wednesday</option>
                <option :value="4">Thursday</option><option :value="5">Friday</option>
                <option :value="6">Saturday</option>
              </select>
            </div>
            <div class="col-12"><label><input type="checkbox" v-model="form.is_enabled" :true-value="1" :false-value="0" /> Enabled</label></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="editModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveJob">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "CronJobsAdmin",
  components: { Navigation },
  data() {
    return {
      tab: 'jobs', ticking: false, saving: false,
      jobs: [], stats: {}, runs: [], runsFilter: '',
      editModal: false, form: {},
      apiBase: (window.location.origin.includes('localhost') ? 'http://localhost' : window.location.origin) + '/apis/hmis/index.php'
    };
  },
  mounted() { this.loadAll(); },
  methods: {
    async loadAll() {
      const r = await executeJson({ function: 'getCronJobs' }, hms);
      if (r.data && r.data.success) {
        this.jobs = r.data.data.Jobs || [];
        this.stats = r.data.data.Stats || {};
      }
    },
    async loadRuns(j) {
      this.runsFilter = j ? j.job_id : '';
      const r = await executeJson({ function: 'getCronRuns', job_id: this.runsFilter || '' }, hms);
      if (r.data && r.data.success) this.runs = r.data.data.Runs || [];
    },
    loadRunsByCode() { this.loadRuns(this.runsFilter ? { job_id: this.runsFilter } : null); },

    async runTick() {
      this.ticking = true;
      try {
        const r = await executeJson({ function: 'runCronTick' }, hms);
        if (r.data && r.data.success) {
          await this.loadAll();
          alert(r.data.message || 'OK');
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.ticking = false; }
    },
    async trigger(j) {
      const r = await executeJson({ function: 'triggerCronJob', job_id: j.job_id }, hms);
      if (r.data && r.data.success) {
        await this.loadAll();
        alert(r.data.message + (r.data.data?.msg ? ' — ' + r.data.data.msg : ''));
      } else alert((r.data && r.data.message) || 'Failed.');
    },
    async toggle(j) {
      const newVal = j.is_enabled == 1 ? 0 : 1;
      const r = await executeJson({ function: 'toggleCronJob', job_id: j.job_id, is_enabled: newVal }, hms);
      if (r.data && r.data.success) await this.loadAll();
    },
    openEdit(j) {
      this.form = { ...j };
      this.editModal = true;
    },
    async saveJob() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveCronJob', ...this.form }, hms);
        if (r.data && r.data.success) { this.editModal = false; await this.loadAll(); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    rowClass(j) {
      if (j.is_enabled != 1) return 'row-muted';
      if (j.consecutive_failures >= 3) return 'row-action';
      if (j.last_run_status === 'failed') return 'row-warn';
      if (j.last_run_status === 'success') return '';
      return '';
    },
    statusPill(s) { return ({ success:'hms-pill-success', failed:'hms-pill-danger', running:'hms-pill-info', skipped:'hms-pill-muted' })[s] || ''; },
    formatSchedule(j) {
      if (j.schedule_type === 'interval_minutes') return `every ${j.interval_minutes}m`;
      if (j.schedule_type === 'daily_at_time') return `daily @ ${(j.schedule_time||'').slice(0,5)}`;
      if (j.schedule_type === 'weekly') return `weekly · ${this.dayName(j.day_of_week)} @ ${(j.schedule_time||'').slice(0,5)}`;
      return 'manual';
    },
    dayName(d) { return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d] || '?'; },
    formatDt(d) { if (!d) return '—'; return new Date(d.replace(' ','T')).toLocaleString(); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 14px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 14px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .4px; margin-top: 2px; }
.hms-stat-success { color: #276749; } .hms-stat-danger { color: #c53030; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }
.hms-table tr.row-good   { background: #f0fff4; }
.hms-table tr.row-muted  { color: #a0aec0; opacity: .65; }

.hms-output-cell { max-width: 360px; white-space: pre-wrap; word-break: break-word; font-size: 12px; color: #4a5568; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }
.hms-section-lbl { font-size: 11px; font-weight: 700; color: #718096; text-transform: uppercase; letter-spacing: .5px; padding-bottom: 3px; border-bottom: 1px solid #e2e8f0; }

.hms-pill { display: inline-flex; align-items: center; gap: 3px; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 16px; font-size: 13px; }
.hms-pre { background: #1a202c; color: #cbd5e0; padding: 12px; border-radius: 6px; font-size: 12px; overflow-x: auto; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 95vh; display: flex; flex-direction: column; overflow: hidden; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-warning   { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.hms-switch { position: relative; display: inline-block; width: 38px; height: 20px; }
.hms-switch input { opacity: 0; width: 0; height: 0; }
.hms-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e0; transition: .3s; border-radius: 20px; }
.hms-slider:before { position: absolute; content: ""; height: 14px; width: 14px; left: 3px; bottom: 3px; background-color: white; transition: .3s; border-radius: 50%; }
.hms-switch input:checked + .hms-slider { background-color: #38a169; }
.hms-switch input:checked + .hms-slider:before { transform: translateX(18px); }

.st-bad { color: #c53030; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
